import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import api from '../api';
import { formatNumber, formatRelativeTime } from '../utils/formatters';

function severityClasses(severity = 'warning') {
  const normalized = String(severity || '').toLowerCase();
  if (normalized === 'critical') {
    return 'border-[#93000a]/60 bg-[#2a1112] text-[#ffdad6]';
  }
  if (normalized === 'warning') {
    return 'border-[#fd6c00]/40 bg-[#2b1403] text-[#ffb692]';
  }
  if (normalized === 'healthy') {
    return 'border-[#f1c100]/20 bg-[#14120a] text-[#f7d88a]';
  }
  return 'border-[#4e4632] bg-[#111111] text-[#d2c5ab]';
}

export default function CommandAlerts() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAlertId, setBusyAlertId] = useState('');
  const [error, setError] = useState('');

  const loadAlerts = useCallback(async ({ force = false } = {}) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const request = force
        ? api.post('/admin/platform/command-alerts/refresh', { range: '24h' })
        : api.get('/admin/platform/command-alerts?range=24h');
      const { data } = await request;
      setPayload(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load command alerts.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadAlerts();
    }, 20_000);
    return () => clearInterval(timer);
  }, [loadAlerts]);

  const alerts = payload?.alerts || [];
  const summary = payload?.summary || null;
  const alertStats = {
    open: alerts.filter((alert) => alert.status === 'open').length,
    acknowledged: alerts.filter((alert) => alert.status === 'acknowledged').length,
    critical: alerts.filter((alert) => alert.severity === 'critical').length,
    products: new Set(alerts.map((alert) => alert.productKey).filter(Boolean)).size,
  };

  const setAlertStatus = async (alertId, status) => {
    setBusyAlertId(alertId);
    setError('');
    try {
      await api.post(`/admin/platform/command-alerts/${alertId}/status`, { status });
      await loadAlerts();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update the alert.');
    } finally {
      setBusyAlertId('');
    }
  };

  if (loading) {
    return (
      <Layout>
        <StatePanel message="Loading command alerts..." />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2rem] border border-[#4e4632] bg-[radial-gradient(circle_at_top_left,rgba(241,193,0,0.16),transparent_35%),linear-gradient(180deg,#121212,#050505)] p-6 shadow-[0_22px_60px_rgba(0,0,0,0.36)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#f1c100]">
                Cross-Product Operations
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Command alerts
              </h1>
              <p className="mt-3 text-sm leading-7 text-[#d2c5ab]">
                These alerts are generated from live Shimbirr Command telemetry and stay separate from
                support or verification queues so operations issues get their own workflow.
              </p>
            </div>

            <button
              type="button"
              onClick={() => loadAlerts({ force: true })}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-full bg-[#ffcc00] px-5 py-3 text-sm font-bold uppercase tracking-[0.16em] text-[#3d2f00] transition hover:brightness-110 disabled:opacity-70"
            >
              <MaterialIcon name={refreshing ? 'sync' : 'refresh'} className={refreshing ? 'animate-spin text-sm' : 'text-sm'} />
              {refreshing ? 'Refreshing' : 'Refresh alerts'}
            </button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Open alerts" value={alertStats.open} accent="text-white" />
            <SummaryCard label="Acknowledged" value={alertStats.acknowledged} accent="text-[#f7d88a]" />
            <SummaryCard label="Critical" value={alertStats.critical} accent="text-[#ffb692]" />
            <SummaryCard label="Products hit" value={alertStats.products} accent="text-[#f1c100]" />
          </div>
        </section>

        {summary ? (
          <CommandAnalyticsStrip
            summary={summary}
            title="Alert source telemetry"
            description="Alerts are derived from freshness, queue pressure, renewals, and source health across the connected product suite."
            onRefresh={() => loadAlerts({ force: true })}
            refreshing={refreshing}
            compact
          />
        ) : null}

        {error ? <StatePanel tone="error" message={error} /> : null}

        <section className="rounded-[2rem] border border-[#4e4632] bg-[#090909] shadow-[0_20px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-3 border-b border-[#4e4632] px-6 py-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-[-0.03em] text-white">Active alert queue</h2>
              <p className="mt-2 text-sm text-[#d2c5ab]">
                Latest update {summary?.lastUpdatedAt ? formatRelativeTime(summary.lastUpdatedAt) : 'not recorded'}
              </p>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9a9078]">
              Auto-refresh every 20 seconds
            </p>
          </div>

          <div className="space-y-4 p-6">
            {alerts.length ? (
              alerts.map((alert) => (
                <article
                  key={alert._id}
                  className={`rounded-[1.6rem] border p-5 ${severityClasses(alert.severity)}`}
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="max-w-3xl">
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="text-lg font-bold text-white">{alert.title}</p>
                        <StatusPill value={alert.status} />
                        <StatusPill value={alert.severity} />
                      </div>
                      <p className="mt-3 text-sm leading-7">{alert.message}</p>
                      <div className="mt-4 flex flex-wrap gap-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">
                        <span>{alert.productLabel || alert.productKey || 'Shimbirr Command'}</span>
                        <span>{alert.ruleType?.replace(/_/g, ' ')}</span>
                        <span>{alert.lastTriggeredAt ? formatRelativeTime(alert.lastTriggeredAt) : 'just now'}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 xl:max-w-sm xl:justify-end">
                      {alert.productKey ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/desk/${alert.productKey}`)}
                          className="rounded-full border border-[#f1c100]/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#f7d88a] transition hover:bg-[#f1c100] hover:text-[#241a00]"
                        >
                          Open desk
                        </button>
                      ) : null}
                      {alert?.data?.actionUrl ? (
                        <a
                          href={alert.data.actionUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-full border border-[#f1c100]/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#f7d88a] transition hover:bg-[#f1c100] hover:text-[#241a00]"
                        >
                          Open source
                        </a>
                      ) : null}
                      {alert.status !== 'acknowledged' ? (
                        <ActionButton
                          busy={busyAlertId === alert._id}
                          label="Acknowledge"
                          onClick={() => setAlertStatus(alert._id, 'acknowledged')}
                        />
                      ) : null}
                      {alert.status !== 'suppressed' ? (
                        <ActionButton
                          busy={busyAlertId === alert._id}
                          label="Suppress"
                          onClick={() => setAlertStatus(alert._id, 'suppressed')}
                        />
                      ) : null}
                      {alert.status !== 'resolved' ? (
                        <ActionButton
                          busy={busyAlertId === alert._id}
                          label="Resolve"
                          onClick={() => setAlertStatus(alert._id, 'resolved')}
                        />
                      ) : null}
                      {alert.status === 'resolved' ? (
                        <ActionButton
                          busy={busyAlertId === alert._id}
                          label="Reopen"
                          onClick={() => setAlertStatus(alert._id, 'open')}
                        />
                      ) : null}
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <StatePanel
                message="No command alerts are open right now. The connected products are currently calm."
              />
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function SummaryCard({ label, value, accent = 'text-white' }) {
  return (
    <div className="rounded-[1.4rem] border border-[#4e4632] bg-[#111111] p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9a9078]">{label}</p>
      <p className={`mt-3 text-3xl font-black tracking-[-0.03em] ${accent}`}>
        {formatNumber(value || 0)}
      </p>
    </div>
  );
}

function StatusPill({ value }) {
  const normalized = String(value || '').toLowerCase();
  return (
    <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${severityClasses(normalized)}`}>
      {normalized.replace(/_/g, ' ') || 'unknown'}
    </span>
  );
}

function ActionButton({ label, onClick, busy = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="rounded-full border border-[#4e4632] bg-[#151515] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:border-[#f1c100]/35 hover:text-[#f7d88a] disabled:opacity-60"
    >
      {busy ? 'Working...' : label}
    </button>
  );
}

function StatePanel({ message, tone = 'default' }) {
  const classes =
    tone === 'error'
      ? 'border-[#93000a]/60 bg-[#2a1112] text-[#ffdad6]'
      : 'border-[#4e4632] bg-[#111111] text-[#d2c5ab]';
  return (
    <div className={`rounded-[1.6rem] border p-6 text-sm ${classes}`}>{message}</div>
  );
}
